import { useEffect, useRef } from "react";

/**
 * Run `tick` on an interval, but only while the tab is actually in front.
 *
 * The console is a screen people leave open — a director keeps it up all shift — and nothing here
 * pushes. Without a heartbeat of its own every unit slides into "offline" as the clock moves past
 * the staleness cut-off, because the page is still holding the roster it fetched hours ago.
 *
 * Pausing on a hidden tab is the other half: a console minimised overnight shouldn't keep polling,
 * and the tick on the way back to visible matters more than the ones it skipped, so returning to
 * the tab refreshes immediately rather than showing stale data until the next interval lands.
 */
export function usePoll(tick: () => void, ms: number | undefined): void {
    const tickRef = useRef(tick);
    tickRef.current = tick;

    useEffect(() => {
        if (!ms) return;
        let timer: number | undefined;

        const stop = () => {
            if (timer !== undefined) window.clearInterval(timer);
            timer = undefined;
        };
        const start = () => {
            stop();
            timer = window.setInterval(() => tickRef.current(), ms);
        };
        const onVisibility = () => {
            if (document.visibilityState !== "visible") {
                stop();
                return;
            }
            tickRef.current();
            start();
        };

        if (document.visibilityState === "visible") start();
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            stop();
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [ms]);
}
